import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Eye, EyeOff } from "lucide-react";

export default function AdminLogin() {
    const { login, user, loading } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (!loading && user && user.role === "admin") {
        return <Navigate to="/admin" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const res = await login(email, password);
        setSubmitting(false);
        if (res.ok) {
            navigate("/admin");
        } else {
            setError(res.error || "Login failed");
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0B] hero-radial flex items-center justify-center p-5" data-testid="admin-login">
            <div className="w-full max-w-md bg-[#121214] border border-[#27272A] rounded-sm p-7">
                <div className="flex items-center gap-3 mb-6">
                    <img src={settings?.logo_url} alt="Opensea" className="h-14 w-auto max-h-14 object-contain" />
                </div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold mb-1">
                    Admin Console
                </div>
                <h1 className="font-display text-3xl font-black uppercase tracking-tight">Sign in</h1>
                <p className="text-white/55 text-sm mt-1">Authorized personnel only.</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="block text-xs uppercase tracking-widest font-display font-bold text-white/65 mb-1.5">
                            Email
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-3 text-white focus:border-[#00BCE5] outline-none"
                            data-testid="login-email"
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest font-display font-bold text-white/65 mb-1.5">
                            Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPw ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-3 text-white focus:border-[#00BCE5] outline-none pr-10"
                                data-testid="login-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((s) => !s)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/55 hover:text-white p-1"
                                aria-label="Toggle"
                                data-testid="login-toggle-password"
                            >
                                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>

                    {error && <div className="text-sm text-[#FF3B30]" data-testid="login-error">{error}</div>}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest py-3.5 rounded-sm hover:bg-[#33C9EA] disabled:opacity-50 btn-primary-glow"
                        data-testid="login-submit"
                    >
                        {submitting ? "Signing in…" : "Sign in"}
                    </button>
                </form>
            </div>
        </div>
    );
}
